/**
 * Utility class for number formatting
 * 
 * From accounting
 * @see https://www.npmjs.com/package/accounting
 */
export class WizNumberFormatter {

  // Contains default parameters for currency and number formatting
  private static defaultSettings = {
    precision: 0,		// default precision on numbers is 0
    thousand: ",",
    decimal: "."
  }

  /**
   * Check and normalise the value of precision (must be positive integer)
   */
  private static checkPrecision(val: number, base: number) {
    val = Math.round(Math.abs(val));
    return isNaN(val) ? base : val;
  }

  /**
   * Implementation of toFixed() that treats floats more like decimals
   *
   * Fixes binary rounding issues (eg. toFixed(0.615,2) === "0.61") that present
   * problems for accounting- and finance-related software.
   */
  private static toFixed(value: number, precision: number) {
    precision = this.checkPrecision(precision, this.defaultSettings.precision);
    const power = Math.pow(10, precision);

    // Multiply up by precision, round accurately, then divide and use native toFixed():
    return (Math.round(value * power) / power).toFixed(precision);
  }

  /**
   * Format a number, with comma-separated thousands and custom precision/decimal places
   *
   * Localise by overriding the precision and thousand / decimal separators
   * 2nd parameter `precision` can be an object matching `settings.number`
   */
  public static formatNumber(value: number, precision: number, thousand: string = null, decimal: string = null) {
    // Clean up precision
    const usePrecision = this.checkPrecision(precision, this.defaultSettings.precision);
    const useThousand = (thousand) ?? this.defaultSettings.thousand;
    const useDecimal = (decimal) ?? this.defaultSettings.decimal;

    // Do some calc
    const negative = value < 0 ? "-" : "";
    const base = parseInt(this.toFixed(Math.abs(value || 0), usePrecision), 10) + "";
    const mod = base.length > 3 ? base.length % 3 : 0;

    // Format the number
    return negative + (mod ? base.substr(0, mod) + useThousand : "") + base.substr(mod).replace(/(\d{3})(?=\d)/g, "$1" + useThousand) + (usePrecision ? useDecimal + this.toFixed(Math.abs(value), usePrecision).split('.')[1] : "");
  }
}